import { Check, FileSpreadsheet, Upload } from "lucide-react";
import {
  useCanAccess,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link, Navigate } from "react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import type { Contact, LeadSource, Sale } from "../types";
import { usePermissions, useRefreshFunnel } from "./hooks";
import {
  IMPORT_TARGETS,
  decodeFile,
  guessMapping,
  parseCsv,
  planImport,
  type ImportPlan,
  type Mapping,
} from "./importLogic";
import { phoneKey } from "./leadLogic";
import { FunnelShell, NativeSelect } from "./shared";
import { useTags } from "../tags/useTags";

type Step = 1 | 2 | 3;
type Parsed = {
  fileName: string;
  headers: string[];
  rows: Record<string, string>[];
};

const SOURCES: LeadSource[] = ["facebook", "instagram", "site", "other"];
const CONCURRENCY = 4;

/** Lead import from CSV, e.g. a Meta Lead Ads export (docs/design/Import) */
export const ImportLeadsPage = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "import",
    action: "create",
  });
  if (isPending) return null;
  if (!canAccess) return <Navigate to="/" />;
  return <ImportWizard />;
};

ImportLeadsPage.path = "/funnel/import";

const ImportWizard = () => {
  const translate = useTranslate();
  const [step, setStep] = useState<Step>(1);
  const [parsed, setParsed] = useState<Parsed>();
  const [mapping, setMapping] = useState<Mapping>({});
  const [source, setSource] = useState<LeadSource>("facebook");
  const [salesId, setSalesId] = useState("");
  const [tagId, setTagId] = useState("");
  const [error, setError] = useState<false | "unreadable" | "empty" | "zip">(
    false,
  );
  const [dragging, setDragging] = useState(false);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(false);
    // Dragged straight out of an archive window: the browser gets nothing
    if (/\.(zip|rar|7z)$/i.test(file.name)) return setError("zip");
    if (file.size === 0) return setError("empty");
    try {
      const { headers, rows } = parseCsv(decodeFile(await file.arrayBuffer()));
      if (headers.length === 0 || rows.length === 0) throw new Error("empty");
      setParsed({ fileName: file.name, headers, rows });
      setMapping(guessMapping(headers));
      setStep(2);
    } catch {
      setError("unreadable");
    }
  };

  const steps: [Step, string][] = [
    [1, translate("crm.import_leads.step_file")],
    [2, translate("crm.import_leads.step_columns")],
    [3, translate("crm.import_leads.step_check")],
  ];

  return (
    <FunnelShell title={translate("crm.import_leads.title")}>
      <p className="-mt-2 text-muted-foreground">
        {translate("crm.import_leads.subtitle")}
      </p>
      <ol className="flex max-w-3xl items-center gap-3">
        {steps.map(([number, label], index) => (
          <li
            key={number}
            className={cn(
              "flex items-center gap-2 font-bold",
              index < steps.length - 1 && "flex-1",
            )}
          >
            <span
              className={cn(
                "flex size-7 items-center justify-center rounded-full text-sm",
                number < step
                  ? "bg-primary text-primary-foreground"
                  : number === step
                    ? "border-2 border-primary text-primary"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {number}
            </span>
            <span className={number > step ? "text-muted-foreground" : ""}>
              {label}
            </span>
            {index < steps.length - 1 ? (
              <span
                className={cn(
                  "h-0.5 flex-1 rounded-full",
                  number < step ? "bg-primary" : "bg-border",
                )}
              />
            ) : null}
          </li>
        ))}
      </ol>

      {step === 1 ? (
        <div className="grid max-w-5xl gap-4 lg:grid-cols-[1fr_340px]">
          <label
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              onFile(event.dataTransfer.files?.[0]);
            }}
            className={cn(
              "group flex min-h-80 cursor-pointer flex-col items-center justify-center gap-4 rounded-3xl border-2 border-dashed p-10 text-center transition-colors",
              dragging
                ? "border-primary bg-primary/10"
                : "border-primary/30 bg-card hover:border-primary/60 hover:bg-primary/5",
            )}
          >
            <span className="flex size-20 items-center justify-center rounded-full bg-primary/10 transition-transform group-hover:scale-105">
              <FileSpreadsheet className="size-9 text-primary" />
            </span>
            <span className="text-xl font-extrabold">
              {translate("crm.import_leads.choose")}
            </span>
            <span className="text-sm text-muted-foreground">
              {translate("crm.import_leads.drop")}
            </span>
            <span className={cn(buttonVariants({ size: "lg" }), "mt-1")}>
              <Upload className="size-4" />
              {translate("crm.import_leads.pick")}
            </span>
            <span className="max-w-md text-xs text-muted-foreground">
              {translate("crm.import_leads.formats")}
            </span>
            <input
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
              className="sr-only"
              onChange={(event) => onFile(event.target.files?.[0])}
            />
            {error ? (
              <span className="text-sm font-semibold text-destructive">
                {translate(`crm.import_leads.${error}`)}
              </span>
            ) : null}
          </label>
          <aside className="flex flex-col gap-4 rounded-3xl border bg-card p-5">
            <div>
              <h2 className="mb-2 font-bold">
                {translate("crm.import_leads.meta_title")}
              </h2>
              <ol className="flex flex-col gap-2 text-sm">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex gap-2">
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-primary">
                      {n}
                    </span>
                    {translate(`crm.import_leads.meta_step_${n}`)}
                  </li>
                ))}
              </ol>
            </div>
            <div className="border-t pt-4">
              <h2 className="mb-2 font-bold">
                {translate("crm.import_leads.what_title")}
              </h2>
              <ul className="flex flex-col gap-1.5 text-sm text-muted-foreground">
                {["dupes", "stage", "date", "quiet", "tag"].map((key) => (
                  <li key={key} className="flex gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                    {translate(`crm.import_leads.what_${key}`)}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      ) : null}

      {step === 2 && parsed ? (
        <ColumnsStep
          parsed={parsed}
          mapping={mapping}
          setMapping={setMapping}
          source={source}
          setSource={setSource}
          salesId={salesId}
          setSalesId={setSalesId}
          tagId={tagId}
          setTagId={setTagId}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      ) : null}

      {step === 3 && parsed ? (
        <CheckStep
          parsed={parsed}
          mapping={mapping}
          source={source}
          salesId={salesId}
          tagId={tagId}
          onBack={() => setStep(2)}
        />
      ) : null}
    </FunnelShell>
  );
};

const ColumnsStep = ({
  parsed,
  mapping,
  setMapping,
  source,
  setSource,
  salesId,
  setSalesId,
  tagId,
  setTagId,
  onBack,
  onNext,
}: {
  parsed: Parsed;
  mapping: Mapping;
  setMapping: (mapping: Mapping) => void;
  source: LeadSource;
  setSource: (source: LeadSource) => void;
  salesId: string;
  setSalesId: (id: string) => void;
  tagId: string;
  setTagId: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) => {
  const { data: tags = [] } = useTags();
  const translate = useTranslate();
  const permissions = usePermissions();
  const { identity } = useGetIdentity();
  const canAssign = permissions.includes("leads.transfer");
  const { data: sales = [] } = useGetList<Sale>(
    "sales",
    { pagination: { page: 1, perPage: 100 } },
    { enabled: canAssign },
  );
  const hasPhone = Object.values(mapping).includes("phone");
  const sample = parsed.rows[0] ?? {};

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
      <section className="min-w-0 rounded-2xl border bg-card p-5">
        <h2 className="mb-4 text-lg font-bold">
          {translate("crm.import_leads.mapping_title")}
        </h2>
        <div className="flex flex-col gap-3">
          {parsed.headers.map((header) => (
            <div
              key={header}
              className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)]"
            >
              <div className="min-w-0">
                <div className="truncate rounded-md bg-muted px-3 py-2 font-mono text-sm">
                  {header}
                </div>
                {sample[header] ? (
                  <div className="truncate px-1 pt-0.5 text-xs text-muted-foreground">
                    {sample[header]}
                  </div>
                ) : null}
              </div>
              <span className="hidden text-center text-muted-foreground sm:block">
                →
              </span>
              <NativeSelect
                aria-label={header}
                value={mapping[header]}
                onChange={(event) =>
                  setMapping({
                    ...mapping,
                    [header]: event.target.value as Mapping[string],
                  })
                }
              >
                {IMPORT_TARGETS.map((target) => (
                  <option key={target} value={target}>
                    {translate(`crm.import_leads.targets.${target}`)}
                  </option>
                ))}
              </NativeSelect>
            </div>
          ))}
        </div>
      </section>
      <section className="flex flex-col gap-3 self-start rounded-2xl border bg-card p-5">
        <h2 className="text-lg font-bold">
          {translate("crm.import_leads.file")}
        </h2>
        <div>
          <div className="font-bold">{parsed.fileName}</div>
          <div className="text-sm text-muted-foreground">
            {translate("crm.import_leads.rows", {
              smart_count: parsed.rows.length,
            })}
          </div>
        </div>
        <label className="flex flex-col gap-1 text-sm font-semibold text-muted-foreground">
          {translate("crm.import_leads.default_source")}
          <NativeSelect
            value={source}
            onChange={(event) => setSource(event.target.value as LeadSource)}
          >
            {SOURCES.map((value) => (
              <option key={value} value={value}>
                {translate(`resources.contacts.sources.${value}`)}
              </option>
            ))}
          </NativeSelect>
        </label>
        {canAssign ? (
          <label className="flex flex-col gap-1 text-sm font-semibold text-muted-foreground">
            {translate("crm.import_leads.manager")}
            <NativeSelect
              value={salesId}
              onChange={(event) => setSalesId(event.target.value)}
            >
              <option value="">{identity?.fullName}</option>
              <option value="none">
                {translate("crm.import_leads.no_manager")}
              </option>
              {sales
                .filter((sale) => !sale.disabled && sale.id !== identity?.id)
                .map((sale) => (
                  <option key={sale.id} value={sale.id}>
                    {sale.first_name} {sale.last_name}
                  </option>
                ))}
            </NativeSelect>
          </label>
        ) : null}
        <label className="flex flex-col gap-1 text-sm font-semibold text-muted-foreground">
          {translate("crm.funnel.tags.import")}
          <NativeSelect
            value={tagId}
            onChange={(event) => setTagId(event.target.value)}
          >
            <option value="">{translate("crm.funnel.tags.import_none")}</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </NativeSelect>
          <span className="text-xs font-normal">
            {translate("crm.funnel.tags.import_hint")}
          </span>
        </label>
        <p className="text-sm text-muted-foreground">
          {translate("crm.import_leads.hint")}
        </p>
        {!hasPhone ? (
          <p className="text-sm font-semibold text-destructive">
            {translate("crm.import_leads.need_phone")}
          </p>
        ) : null}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onBack}>
            {translate("ra.action.back")}
          </Button>
          <Button type="button" onClick={onNext} disabled={!hasPhone}>
            {translate("crm.import_leads.next")}
          </Button>
        </div>
      </section>
    </div>
  );
};

const CheckStep = ({
  parsed,
  mapping,
  source,
  salesId,
  tagId,
  onBack,
}: {
  parsed: Parsed;
  mapping: Mapping;
  source: LeadSource;
  salesId: string;
  tagId: string;
  onBack: () => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const refresh = useRefreshFunnel();
  const { identity } = useGetIdentity();
  // Every lead the user can see, to skip numbers already in the base
  const { data: contacts, isPending } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "id", order: "ASC" },
  });
  const [progress, setProgress] = useState<{ done: number; failed: number }>();
  const [finished, setFinished] = useState(false);
  // Frozen when the import starts: the contact list refetches during the run
  // and would otherwise count the new leads as duplicates
  const [running, setRunning] = useState<ImportPlan>();
  const [locale] = useLocaleState();

  if (isPending) return null;
  const existing = new Set(
    (contacts ?? []).flatMap((contact) =>
      (contact.phone_jsonb ?? []).map((phone) => phoneKey(phone.number)),
    ),
  );
  const plan: ImportPlan =
    running ?? planImport(parsed.rows, mapping, existing, source);
  const owner =
    salesId === "none" ? null : salesId ? Number(salesId) : identity?.id;

  const run = async () => {
    let done = 0;
    let failed = 0;
    setRunning(plan);
    setProgress({ done, failed });
    const queue = [...plan.leads];
    const worker = async () => {
      for (let lead = queue.shift(); lead; lead = queue.shift()) {
        try {
          const created = lead.created_at ?? new Date().toISOString();
          const { data: contact } = await dataProvider.create<Contact>(
            "contacts",
            {
              data: {
                first_name:
                  lead.first_name || translate("crm.funnel.new.no_name"),
                last_name: lead.last_name,
                phone_jsonb: [{ number: lead.phone, type: "Work" }],
                email_jsonb: lead.email
                  ? [{ email: lead.email, type: "Work" }]
                  : [],
                source: lead.source,
                source_form: lead.source_form,
                campaign: lead.campaign,
                utm: lead.utm,
                rooms_wanted: lead.rooms_wanted,
                sales_id: owner,
                tags: tagId ? [Number(tagId)] : [],
                created_at: created,
                first_seen: created,
                last_seen: created,
              },
            },
          );
          await dataProvider.create("deals", {
            data: {
              name: [contact.first_name, contact.last_name]
                .filter(Boolean)
                .join(" "),
              stage: "new",
              contact_ids: [contact.id],
              sales_id: owner,
              index: 0,
              created_at: created,
              stage_changed_at: created,
            },
          });
          if (lead.comment) {
            await dataProvider.create("contact_notes", {
              data: {
                contact_id: contact.id,
                text: lead.comment,
                date: created,
              },
            });
          }
          done++;
        } catch {
          failed++;
        }
        setProgress({ done, failed });
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    await refresh();
    setFinished(true);
  };

  const stats: [string, number, string?][] = [
    ["crm.import_leads.will_import", plan.leads.length, "text-primary"],
    ["crm.import_leads.no_phone", plan.noPhone],
    ["crm.import_leads.dup_base", plan.duplicatesInBase],
    ["crm.import_leads.dup_file", plan.duplicatesInFile],
  ];

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map(([key, value, tone]) => (
          <div key={key} className="rounded-2xl border bg-card p-4">
            <div className={cn("text-3xl font-extrabold", tone)}>{value}</div>
            <div className="text-sm text-muted-foreground">
              {translate(key)}
            </div>
          </div>
        ))}
      </section>

      {plan.leads.length > 0 && !progress ? (
        <section className="overflow-x-auto rounded-2xl border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">
                  {translate("crm.import_leads.targets.full_name")}
                </th>
                <th className="px-4 py-3">
                  {translate("crm.import_leads.targets.phone")}
                </th>
                <th className="px-4 py-3">
                  {translate("crm.import_leads.targets.source")}
                </th>
                <th className="px-4 py-3">
                  {translate("crm.import_leads.targets.created_at")}
                </th>
              </tr>
            </thead>
            <tbody>
              {plan.leads.slice(0, 5).map((lead) => (
                <tr key={lead.phone} className="border-b last:border-0">
                  <td className="px-4 py-2 font-semibold">
                    {lead.first_name} {lead.last_name}
                  </td>
                  <td className="px-4 py-2">{lead.phone}</td>
                  <td className="px-4 py-2">
                    {translate(`resources.contacts.sources.${lead.source}`)}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {lead.created_at
                      ? new Date(lead.created_at).toLocaleDateString(locale)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {plan.leads.length > 5 ? (
            <p className="px-4 py-2 text-xs text-muted-foreground">
              {translate("crm.import_leads.and_more", {
                count: plan.leads.length - 5,
              })}
            </p>
          ) : null}
        </section>
      ) : null}

      {progress ? (
        <section className="flex flex-col gap-2 rounded-2xl border bg-card p-5">
          <Progress
            value={
              (100 * (progress.done + progress.failed)) /
              (plan.leads.length || 1)
            }
          />
          <div className="text-sm">
            {translate("crm.import_leads.progress", {
              done: progress.done,
              total: plan.leads.length,
            })}
            {progress.failed > 0
              ? ` · ${translate("crm.import_leads.failed", { count: progress.failed })}`
              : ""}
          </div>
          {finished ? (
            <Link to="/funnel" className={cn(buttonVariants(), "self-start")}>
              {translate("crm.import_leads.to_funnel")}
            </Link>
          ) : null}
        </section>
      ) : (
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onBack}>
            {translate("ra.action.back")}
          </Button>
          <Button
            type="button"
            onClick={run}
            disabled={plan.leads.length === 0}
          >
            {translate("crm.import_leads.run", {
              smart_count: plan.leads.length,
            })}
          </Button>
        </div>
      )}
    </div>
  );
};
